import { useState } from "react";
import { Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/Auth";
import { errorText } from "@/lib/api";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const location = useLocation();
  const [error, setError] = useState(location.state?.error || "");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin" replace />;

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await login(email, password); nav("/admin"); }
    catch (err) { setError(errorText(err, "No se pudo iniciar sesión")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen hero-bg grid place-items-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-xl" data-testid="admin-login-form">
        <img src="/brand/sello-sm.webp" alt="La Patrona" className="w-16 h-16 rounded-full object-cover mx-auto" />
        <h1 className="mt-4 text-center font-display text-3xl">Panel privado</h1>
        <p className="text-center text-sm text-ink/60 mt-1">Accede para gestionar solicitudes y catálogo</p>
        <button type="button" onClick={googleLogin} className="mt-8 w-full h-11 rounded-full border border-ink/15 bg-white flex items-center justify-center gap-3 text-sm font-medium hover:bg-blush/40 transition-colors" data-testid="admin-login-google-btn">
          <GoogleIcon /> Entrar con Google
        </button>
        <div className="my-5 flex items-center gap-3 text-xs text-ink/40"><span className="flex-1 h-px bg-ink/10" />o con tu contraseña<span className="flex-1 h-px bg-ink/10" /></div>
        <div className="space-y-4">
          <Input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" />
          <Input type="password" required placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" />
          {error && <p className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11" data-testid="admin-login-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
          </Button>
        </div>
        <Link to="/" className="block mt-6 text-center text-xs text-ink/50 hover:text-granate" data-testid="admin-login-back-link">Volver a la web</Link>
      </form>
    </div>
  );
}
